import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CreateObjectButton } from '@/components/CreateObjectButton'
import { Link as RouterLink, useNavigate } from 'react-router'
import { useCreateDialog, useEditRoute } from '@/contexts/CreateDialogContext'
import posthog from 'posthog-js'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Compass, Users, Flag, SlidersHorizontal, EyeOff, Pencil, Link, Plus, Check, Table, Loader2, Settings, ChartLine, ArrowUpRight } from 'lucide-react'
import { AuthService } from '@/services/core/auth'
import { SitesApi, SiteInput, Site } from '@/services/sites/sitesApi'
import { ApiError, apiErrorDescription } from '@/services/core/apiError'
import { AdUnitsApi, AdUnit } from '@/services/adUnits/adUnitsApi'
import { AppShell } from '../../components/AppShell'
import { SearchInput } from '../../components/SearchInput'
import { filterBySearch } from '../../utils/search'
import { ListTabs } from '../../components/ListTabs'
import { FormSkeleton } from '../../components/FormSkeleton'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import {
  StatsBar,
  StatsBarId,
  StatsBarTraffic,
  StatsBarActivity,
} from '../../components/StatsBar'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { AdUnitTable } from '../../components/AdUnitTable'
import { BulkActionBar } from '../../components/BulkActionBar'
import { AdUnitTagDownloadDialog } from '../../components/AdUnitTagDownloadDialog'
import { StatsPanel } from '../../components/StatsPanel'
import { RouteTabs } from '../../components/RouteTabs'
import { useCurrentUser } from '../../contexts/UserContext'
import { TeamsApi } from '@/services/users/teamsApi'
import { EarningsAccountsApi } from '@/services/earnings/earningsAccountsApi'
import { RulesetsApi } from '@/services/rules/rulesetsApi'
import { useNetwork } from '../../contexts/NetworkContext'
import { isSuperAdmin } from '../../data/superAdmins'
import { MultiSelect } from '@/components/ui/multi-select'
import { SiteGroupsApi } from '@/services/sites/siteGroupsApi'
import { ChipMultiSelect } from '../../components/ChipMultiSelect'
import { COUNTRIES } from '../../data/countries.tsx'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { formatDate } from '../../utils/format'
import { ValidationWarnings } from '@/components/ValidationWarnings'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'

const posthogConfigured = Boolean(
  import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN && import.meta.env.VITE_PUBLIC_POSTHOG_HOST
)

function RulesetsCard({
  form,
  rulesets,
  defaultExpanded,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any
  rulesets: { id: string; name: string; type: string; objectState: string }[]
  defaultExpanded: boolean
}) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(defaultExpanded)

  return (
    <Card tone="delivery">
      <CardHeader>
        <CardTitle>
          <CardIcon icon={SlidersHorizontal} />
          {t('rulesetCard.title')}
        </CardTitle>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? <EyeOff className="size-4" /> : <Pencil className="size-4" />}
          {expanded ? t('rulesetCard.hideRulesets') : t('rulesetCard.editRulesets')}
        </Button>
      </CardHeader>
      {expanded && (
        <CardContent>
          <div className="field-grid">
            <div className="p-field full">
              <label>{t('publishers.inventoryRuleset')}</label>
              <form.Field name="siteRulesetId">
                {(field: any) => (
                  <div className="flex gap-2">
                    <Select label={t('publishers.inventoryRuleset')} value={field.state.value} onValueChange={(v: string) => field.handleChange(v === '__clear__' ? '' : v)}>
                      <SelectTrigger><SelectValue placeholder={t('publishers.selectInventoryRuleset')}>{rulesets.find((r) => r.id === field.state.value)?.name ?? null}</SelectValue></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__clear__">&mdash;</SelectItem>
                        {rulesets.filter((r) => r.type === 'INVENTORY' && r.objectState === 'ACTIVE').map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {field.state.value && (
                      <Button asChild variant="outline" size="icon" className="shrink-0" title={t('rulesets.openRuleset')}>
                        <RouterLink to={`/publishing/rulesets/${field.state.value}`} aria-label={t('rulesets.openRuleset')}>
                          <ArrowUpRight className="size-4" />
                        </RouterLink>
                      </Button>
                    )}
                  </div>
                )}
              </form.Field>
            </div>
            <div className="p-field full">
              <label>{t('publishers.targetingRulesets')}</label>
              <form.Field name="targetingRulesetIds">
                {(field: any) => (
                  <MultiSelect
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    options={rulesets.filter((r) => r.type === 'TARGETING' && r.objectState === 'ACTIVE').map((r) => ({ label: r.name, value: r.id }))}
                    href={(rulesetId) => `/publishing/rulesets/${rulesetId}`}
                    placeholder={t('publishers.selectTargetingRulesets')}
                                    display="chip"
                  />
                )}
              </form.Field>
            </div>
            <div className="p-field full">
              <label>{t('publishers.sizeRulesets')}</label>
              <form.Field name="sizeRulesetIds">
                {(field: any) => (
                  <MultiSelect
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    options={rulesets.filter((r) => r.type === 'SIZE' && r.objectState === 'ACTIVE').map((r) => ({ label: r.name, value: r.id }))}
                    href={(rulesetId) => `/publishing/rulesets/${rulesetId}`}
                    placeholder={t('publishers.selectSizeRulesets')}
                                    display="chip"
                  />
                )}
              </form.Field>
            </div>
            <div className="p-field full">
              <label>{t('publishers.layoutRulesets')}</label>
              <form.Field name="layoutRulesetIds">
                {(field: any) => (
                  <MultiSelect
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    options={rulesets.filter((r) => r.type === 'LAYOUT' && r.objectState === 'ACTIVE').map((r) => ({ label: r.name, value: r.id }))}
                    href={(rulesetId) => `/publishing/rulesets/${rulesetId}`}
                    placeholder={t('publishers.selectLayoutRulesets')}
                                    display="chip"
                  />
                )}
              </form.Field>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  )
}

// ── Inner form component ────────────────────────────────────────────────────

interface SiteEditFormProps {
  site: Site | null
  id: string
  isNew: boolean
  initialEarningsAccountId: string
}

function SiteEditForm({ site, id, isNew, initialEarningsAccountId }: SiteEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createDialog = useCreateDialog()
  // A new object's cancel closes the create dialog, or leaves its page.
  const cancelNew = (path: string) => (createDialog ? createDialog.close() : navigate(path))
  const queryClient = useQueryClient()

  const currentUser = useCurrentUser()
  const showAdmin = isSuperAdmin(currentUser?.username)
  const hasToken = AuthService.isAuthenticated()
  const { data: earningsAccounts = [] } = useQuery({
    queryKey: ['earningsAccounts'],
    queryFn: () => EarningsAccountsApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })
  const { data: siteGroups = [] } = useQuery({
    queryKey: ['site-groups'],
    queryFn: () => SiteGroupsApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })
  const { data: rulesets = [] } = useQuery({
    queryKey: ['rulesets'],
    queryFn: () => RulesetsApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })
  const { data: allTeams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => TeamsApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })
  const advertiserTeams = allTeams.filter((t) => t.type === 'AX_ADVERTISER')
  const [adUnitSelection, setAdUnitSelection] = useState<AdUnit[]>([])
  const [adUnitSearch, setAdUnitSearch] = useState('')
  const [adUnitBulkLoading, setAdUnitBulkLoading] = useState(false)
  const [adUnitTagDownloadOpen, setAdUnitTagDownloadOpen] = useState(false)
  const [showActiveOnly, setShowActiveOnly] = useState(false)

  const politicalAdOptions = [
    { label: t('publishers.policyAllowed'), value: 'ALLOWED' },
    { label: t('publishers.policyRequiresApproval'), value: 'REQUIRES_APPROVAL' },
    { label: t('publishers.policyBlocked'), value: 'BLOCKED' },
  ]

  const adUnitBulkUpdate = async (label: string, payload: Record<string, unknown>) => {
    setAdUnitBulkLoading(true)
    try {
      await Promise.all(
        adUnitSelection.map((a) => AdUnitsApi.update(a.id, { name: a.name, ...payload }))
      )
      queryClient.invalidateQueries({ queryKey: ['adUnits'] })
      toast.success(t('common.done'), {
        description: `${adUnitSelection.length} ad unit(s) updated: ${label}.`,
        duration: 3000,
      })
      setAdUnitSelection([])
    } catch (e) {
      toast.error(t('common.error'), {
        description: apiErrorDescription(e),
        duration: 5000,
      })
    } finally {
      setAdUnitBulkLoading(false)
    }
  }
  const { currentNetwork } = useNetwork()
  const isMarketplace = currentNetwork?.marketplacePlatform ?? false

  const form = useForm({
    defaultValues: {
      name: site?.name ?? '',
      country: site?.country ?? '',
      earningsAccountId: site?.earningsAccountId ?? initialEarningsAccountId,
      politicalAdvertising: site?.politicalAdvertising ?? '',
      paywalled: site?.paywalled ?? false,
      filterTeamsType: site?.filterTeamsType || 'INHERITED' as string,
      filterTeams: site?.filterTeams ?? [] as string[],
      siteGroupIds: site?.siteGroups.map((g) => g.id) ?? [] as string[],
      siteRulesetId: site?.siteRuleset?.id ?? '' as string,
      targetingRulesetIds: site?.targetingRulesets.map((r) => r.id) ?? [] as string[],
      sizeRulesetIds: site?.sizeRulesets.map((r) => r.id) ?? [] as string[],
      layoutRulesetIds: site?.layoutRulesets.map((r) => r.id) ?? [] as string[],
    },
    onSubmit: ({ value }) => {
      mutation.mutate({
        name: value.name,
        country: value.country || undefined,
        earningsAccountId: value.earningsAccountId || undefined,
        paywalled: value.paywalled,
        objectState: (site?.objectState ?? 'ACTIVE') as ObjectState,
        // Site groups are the network's own grouping of its own sites, which
        // is why a marketplace network has none to offer.
        ...(!isMarketplace && { siteGroups: value.siteGroupIds }),
        ...(isMarketplace &&
          value.politicalAdvertising && { politicalAdvertising: value.politicalAdvertising }),
        ...(isMarketplace && {
          filterTeamsType: value.filterTeamsType,
          filterTeams:
            value.filterTeamsType !== 'INHERITED' ? value.filterTeams.map((id) => ({ id })) : [],
          siteRuleset: value.siteRulesetId ? { id: value.siteRulesetId } : null,
          targetingRulesets: value.targetingRulesetIds.map((id) => ({ id })),
          sizeRulesets: value.sizeRulesetIds.map((id) => ({ id })),
          layoutRulesets: value.layoutRulesetIds.map((id) => ({ id })),
        }),
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => SitesApi.update(id!, { name: site?.name ?? '', objectState }),
    ['site', id],
    ['sites']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const { data: allAdUnits = [], isLoading: adUnitsLoading } = useQuery({
    queryKey: ['adUnits'],
    queryFn: () => AdUnitsApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })
  const siteAdUnits = allAdUnits.filter((a) => a.site?.id === id)
  const filteredAdUnits = filterBySearch(
    showActiveOnly ? siteAdUnits.filter((a) => a.objectState === 'ACTIVE') : siteAdUnits,
    adUnitSearch
  )

  const mutation = useMutation({
    mutationFn: (input: SiteInput) =>
      isNew ? SitesApi.create(id!, input) : SitesApi.update(id!, input),
    onSuccess: (data) => {
      if (posthogConfigured) posthog.capture(isNew ? 'site_created' : 'site_updated', {
        is_marketplace: isMarketplace,
        has_earnings_account: Boolean(data.earningsAccountId),
        site_group_count: data.siteGroups.length,
      })
      queryClient.setQueryData(['site', id], data)
      queryClient.setQueryData(['sites'], (old: Site[] | undefined) => {
        if (!old) return [data]
        const idx = old.findIndex((s) => s.id === data.id)
        return idx >= 0 ? old.map((s) => (s.id === data.id ? data : s)) : [...old, data]
      })
      markClean()
      if (isNew) {
        navigate(`/publishing/sites/${id}`, { replace: !createDialog })
      }
      toast.success(t('common.saved'), {
        description: isNew ? t('sites.createdSuccess') : t('sites.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError
          ? error.message
          : isNew
            ? t('sites.createFailed')
            : t('sites.saveFailed')
      const detail = apiErrorDescription(error)
      toast.error(summary, {
        description: detail,
        duration: 5000,
      })
    },
  })

  const crumbs = [
    ...(site?.earningsAccountId
      ? [
          {
            label: site.earningsAccountName || site.earningsAccountId,
            href: `/publishing/earnings/${site.earningsAccountId}`,
          },
        ]
      : [{ label: t('nav.publishers'), href: '/publishing/earnings' }]),
    { label: isNew ? t('sites.newTitle') : (site?.name ?? t('sites.editTitle')) },
  ]

  return (
    <AppShell crumbs={crumbs} showBack objectType={isNew ? undefined : t('objectTypes.site')}>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={site?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.site')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('sites.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {site && !isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={site!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {site && !isNew && (
          <StatsBar
            items={[
              {
                label: t('sites.traffic'),
                content: <StatsBarTraffic active={site.requestedInLastDay} />,
              },
              {
                label: t('common.id'),
                content: <StatsBarId value={site.id} />,
              },
              {
                label: t('sites.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(site.createTime)}
                    updated={formatDate(site.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <div className="ad-edit-columns">
          {/* Left column: details + info */}
          <div className="ad-edit-columns__info">
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Compass} />
                  {t('sites.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <label>{t('common.country')}</label>
                    <form.Field name="country">
                      {(field) => (
                        <Select label={t('common.country')} value={field.state.value} onValueChange={(v) => field.handleChange(v === '__clear__' ? '' : (v ?? ''))}>
                          <SelectTrigger><SelectValue placeholder={t('sites.selectCountry')}>{COUNTRIES.find((o) => o.value === field.state.value)?.label ?? null}</SelectValue></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__clear__">&mdash;</SelectItem>
                            {COUNTRIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field full">
                    <label>{t('sites.publisher')}</label>
                    <form.Field name="earningsAccountId">
                      {(field) => (
                        <Select label={t('sites.publisher')} value={field.state.value} onValueChange={(v) => field.handleChange(v === '__clear__' ? '' : (v ?? ''))}>
                          <SelectTrigger>
                            <SelectValue placeholder={t('sites.selectPublisher')}>
                              {field.state.value ? (earningsAccounts.find((o) => o.id === field.state.value)?.name ?? field.state.value) : null}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__clear__">&mdash;</SelectItem>
                            {earningsAccounts.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                  </div>
                  {!isMarketplace && (
                    <div className="p-field full">
                      <label>{t('sites.siteGroups')}</label>
                      <form.Field name="siteGroupIds">
                        {(field) => (
                          <MultiSelect
                            value={field.state.value}
                            onChange={(v) => field.handleChange(v)}
                            options={siteGroups.map((g) => ({ label: g.name, value: g.id }))}
                            placeholder={t('sites.selectSiteGroups')}
                            display="chip"
                          />
                        )}
                      </form.Field>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Political ads settings — marketplace only */}
            {isMarketplace && (
              <Card tone="attention">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Flag} />
                    {t('sites.politicalAds')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="field-grid">
                    <div className="p-field full">
                      <label>{t('sites.politicalAdsPolicy')}</label>
                      <form.Field name="politicalAdvertising">
                        {(field) => (
                          <Select label={t('sites.politicalAdsPolicy')} value={field.state.value} onValueChange={(v) => field.handleChange(v === '__clear__' ? '' : (v ?? ''))}>
                            <SelectTrigger><SelectValue placeholder={t('sites.selectPolicy')}>{politicalAdOptions.find((o) => o.value === field.state.value)?.label ?? null}</SelectValue></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__clear__">&mdash;</SelectItem>
                              {politicalAdOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Team filtering — marketplace only */}
            {isMarketplace && (
              <Card tone="people">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Users} />
                    {t('sites.advertiserAccess')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="field-grid">
                    <form.Field name="filterTeamsType">
                      {(typeField) =>
                        typeField.state.value === 'INHERITED' ? (
                          <div className="p-field full">
                            <p style={{ margin: 0, fontSize: 13, color: 'var(--ad-text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Link className="size-3" />
                              {t('sites.teamAccessInherited')}
                            </p>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              style={{ marginTop: 8 }}
                              onClick={() => typeField.handleChange('NONE')}
                            >
                              <Pencil className="size-4" />
                              {t('sites.customise')}
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="p-field full">
                              <label>{t('sites.filterType')}</label>
                              <Select label={t('sites.filterType')} value={typeField.state.value} onValueChange={(v) => {
                                  typeField.handleChange(v ?? '')
                                  if (v === 'INHERITED') {
                                    form.setFieldValue('filterTeams', [])
                                  }
                                }}>
                                <SelectTrigger><SelectValue>{[
                                    { label: t('sites.inheritedFromPublisher'), value: 'INHERITED' },
                                    { label: t('sites.onlyAllowSelected'), value: 'NONE' },
                                    { label: t('sites.allExceptSelected'), value: 'EXCLUDES' },
                                  ].find((o) => o.value === typeField.state.value)?.label ?? null}</SelectValue></SelectTrigger>
                                <SelectContent>
                                  {[
                                    { label: t('sites.inheritedFromPublisher'), value: 'INHERITED' },
                                    { label: t('sites.onlyAllowSelected'), value: 'NONE' },
                                    { label: t('sites.allExceptSelected'), value: 'EXCLUDES' },
                                  ].map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="p-field full">
                              <label>
                                {typeField.state.value === 'NONE'
                                  ? t('sites.teamsToAllow')
                                  : t('sites.teamsToExclude')}
                              </label>
                              <form.Field name="filterTeams">
                                {(field) => (
                                  <ChipMultiSelect
                                    value={field.state.value}
                                    onChange={(e) => field.handleChange((e.value as string[]) ?? [])}
                                    options={advertiserTeams}
                                    optionLabel="name"
                                    optionValue="id"
                                    placeholder={t('sites.selectTeams')}
                                  />
                                )}
                              </form.Field>
                            </div>
                          </>
                        )
                      }
                    </form.Field>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Rulesets — marketplace only */}
            {isMarketplace && (
              <form.Subscribe
                selector={(s) => ({
                  hasRulesets:
                    !!s.values.siteRulesetId ||
                    s.values.targetingRulesetIds.length > 0 ||
                    s.values.sizeRulesetIds.length > 0 ||
                    s.values.layoutRulesetIds.length > 0,
                })}
              >
                {({ hasRulesets }) => (
                  <RulesetsCard
                    form={form}
                    rulesets={rulesets}
                    defaultExpanded={hasRulesets}
                  />
                )}
              </form.Subscribe>
            )}

            {/* Ad units belonging to this site */}
            {!isNew && (
              <Card tone="delivery" className="overflow-hidden">
                <CardHeader className="flex flex-row flex-wrap items-center gap-3">
                  <CardTitle>
                    <CardIcon icon={Table} />
                    {t('nav.adUnits')}
                    {siteAdUnits.length > 0 && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: 'var(--ad-text-muted)',
                          background: 'var(--ad-neutral-soft)',
                          padding: '1px 8px',
                          borderRadius: 999,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {siteAdUnits.length}
                      </span>
                    )}
                  </CardTitle>
                  <div className="ml-auto flex items-center gap-2">
                    <ListTabs
                      tabs={[
                        { key: 'all', label: t('common.all') },
                        { key: 'active', label: t('common.activeOnly') },
                      ]}
                      value={showActiveOnly ? 'active' : 'all'}
                      onValueChange={(v) => setShowActiveOnly(v === 'active')}
                    />
                    <SearchInput value={adUnitSearch} onChange={setAdUnitSearch} />
                    <CreateObjectButton type="adUnit" variant="outline" context={{ site: id }} />
                  </div>
                </CardHeader>
                <BulkActionBar
                  count={adUnitSelection.length}
                  actions={[
                    {
                      label: t('common.activate'),
                      icon: 'pi pi-check-circle',
                      severity: 'success',
                      onClick: () => adUnitBulkUpdate('activated', { objectState: 'ACTIVE' }),
                    },
                    {
                      label: t('common.deactivate'),
                      icon: 'pi pi-ban',
                      severity: 'danger',
                      onClick: () => adUnitBulkUpdate('deactivated', { objectState: 'INACTIVE' }),
                    },
                    {
                      label: t('adUnitTags.action'),
                      icon: 'download',
                      onClick: () => setAdUnitTagDownloadOpen(true),
                    },
                  ]}
                  onClear={() => setAdUnitSelection([])}
                  loading={adUnitBulkLoading}
                />
                <AdUnitTagDownloadDialog
                  adUnits={adUnitSelection}
                  open={adUnitTagDownloadOpen}
                  onOpenChange={setAdUnitTagDownloadOpen}
                />
                <CardContent className="p-0">
                  <AdUnitTable
                    adUnits={filteredAdUnits}
                    loading={adUnitsLoading}
                    selection={adUnitSelection}
                    onSelectionChange={setAdUnitSelection}
                    emptyMessage={t('sites.noAdUnitsYet')}
                  />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right column: tabbed panel */}
          {!isNew && id && (
            <div className="ad-panel">
              <RouteTabs
                tabs={[
                  {
                    key: 'statistics',
                    label: t('sites.statistics'),
                    icon: <ChartLine />,
                    content: () => (
                      <StatsPanel
                        query={{ siteId: id, groupBy: 'HALF_HOURLY', includeChunkRange: true }}
                        storageKey="site"
                      />
                    ),
                  },
                  ...(showAdmin
                    ? [
                        {
                          key: 'admin',
                          label: t('sites.adminTab'),
                          icon: <Settings />,
                          content: () => (
                            <Card>
                              <CardHeader>
                                <CardTitle>
                                  <CardIcon icon={Settings} />
                                  {t('sites.adminSettings')}
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div
                                  style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                                >
                                  <form.Field name="paywalled">
                                    {(field) => (
                                      <>
                                        <Switch
                                          checked={field.state.value}
                                          onCheckedChange={(v) => field.handleChange(v)}
                                        />
                                        <label style={{ margin: 0 }}>{t('sites.paywalledContent')}</label>
                                      </>
                                    )}
                                  </form.Field>
                                </div>
                              </CardContent>
                            </Card>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          )}
        </div>

        {/* Save bar */}
        <div className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}>
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => cancelNew('/publishing/sites')}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
            >
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('sites.createSite') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function SiteEditPage() {
  const { t } = useTranslation()
  const { id, searchParams } = useEditRoute()
  const isNew = searchParams.get('new') === 'true'
  const initialEarningsAccountId = isNew ? (searchParams.get('earningsAccount') ?? '') : ''
  const hasToken = AuthService.isAuthenticated()

  const { data: site, isLoading, isError } = useQuery({
    queryKey: ['site', id],
    queryFn: () => SitesApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('nav.publishers'), href: '/publishing/earnings' },
    { label: isNew ? t('sites.newTitle') : t('sites.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={2} columns={2} />
      </AppShell>
    )
  }

  return (
    <SiteEditForm
      key={id}
      site={site ?? null}
      id={id!}
      isNew={isNew}
      initialEarningsAccountId={initialEarningsAccountId}
    />
  )
}
